/**
 * Patient context chip + search/switch, rendered once in the patient-level
 * header (`StandaloneHeader`) so it stays visible across every tab (BLA-334
 * — it previously lived inside each tab's own local header row and vanished
 * on Assessments, which renders none).
 *
 * Shows the active patient (avatar initial + id) on the right, per concept.html,
 * and reuses Home's `PatientSearch` to switch patients. Selecting a result
 * updates the `patient_id` query param so the whole workspace re-scopes; the
 * active tab is preserved by the parent (it lives in component state, not URL).
 */
import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PatientSearch from "../standalone/PatientSearch";
import { recordRecentPatient } from "../../utils/recentPatients";
import { resolvePatientName } from "../../utils/patientNames";

/** Props for {@link PatientContextChip}. */
interface PatientContextChipProps {
  /** The currently active patient id. */
  patientId: string;
  /** Optional display name for the patient (falls back to dev resolution or the id). */
  patientName?: string | null;
}

/**
 * Render the patient search box and the active-patient context chip.
 *
 * @param props - The active patient id and optional display name.
 * @returns The header chip + search element.
 */
export const PatientContextChip: React.FC<PatientContextChipProps> = ({
  patientId,
  patientName,
}) => {
  const [, setSearchParams] = useSearchParams();

  // Resolve display name: prop -> dev/recents lookup -> patientId fallback.
  const resolvedDisplayName = useMemo(() => {
    if (patientName?.trim()) return patientName.trim();
    return resolvePatientName(patientId) ?? null;
  }, [patientId, patientName]);

  // Backfill the display name into Recent Patients once resolved.
  useEffect(() => {
    if (resolvedDisplayName) {
      recordRecentPatient(patientId, resolvedDisplayName);
    }
  }, [patientId, resolvedDisplayName]);

  const handleSelectPatient = (id: string): void => {
    const name = resolvePatientName(id);
    recordRecentPatient(id, name);
    setSearchParams({ patient_id: id });
  };

  const label = resolvedDisplayName || patientId;
  const initial = label.charAt(0).toUpperCase();

  return (
    <div className="flex items-center gap-4 flex-1 min-w-0">
      <PatientSearch onSelectPatient={handleSelectPatient} />
      {/* Hidden below `lg`: on a narrow viewport the search box needs the
       * room this name/id/avatar block would otherwise take; matches the
       * "Back to Home" label breakpoint in StandaloneHeader. */}
      <div className="hidden lg:flex items-center gap-3 border-l border-slate-200 pl-4 h-10 shrink-0">
        <div className="text-right leading-tight">
          <div className="text-sm font-bold text-slate-800">{label}</div>
          <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mt-0.5">
            {patientId}
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold border border-blue-200 text-sm shrink-0">
          {initial}
        </div>
      </div>
    </div>
  );
};

export default PatientContextChip;
